import { Skeleton } from "@/components/ui/skeleton";
import { headerList } from "./rosterTable.config";

const ACTION_BUTTON_WIDTHS = ["104px", "128px", "104px", "104px"];
const SKELETON_HEADERS = headerList
  .filter((header) => header.label.trim().toLowerCase() !== "checkbox")
  .slice(1);

function getColumnFlex(width: string) {
  const numericWidth = Number.parseInt(width, 10);
  return Number.isFinite(numericWidth) ? numericWidth : 80;
}

function HeaderSkeleton() {
  return (
    <div className="mb-6 mt-5 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-3.5">
        <div className="flex items-center gap-2">
          <Skeleton className="size-5 rounded-full bg-zinc-200" />
          <Skeleton className="h-3.5 w-7 bg-zinc-200" />
        </div>
        <div className="flex h-9 w-[170px] rounded-full border border-zinc-200 bg-white p-1">
          <Skeleton className="h-full flex-1 rounded-full bg-zinc-200" />
          <div className="w-2" />
          <Skeleton className="h-full flex-1 rounded-full bg-zinc-100" />
        </div>
      </div>

      <div className="flex items-center gap-3.5">
        {ACTION_BUTTON_WIDTHS.map((width, index) => (
          <Skeleton
            key={`${width}-${index}`}
            className="h-9 rounded-full bg-zinc-200"
            style={{ width }}
          />
        ))}
      </div>
    </div>
  );
}

function TableHeaderSkeleton() {
  return (
    <div className="flex h-9 items-center rounded-t-lg bg-zinc-100 px-2">
      {SKELETON_HEADERS.map((header, index) => (
        <div
          key={`${header.label}-${index}`}
          className="flex h-full min-w-0 items-center justify-center px-1"
          style={{ flex: `${getColumnFlex(header.width)} 1 0` }}
        >
          <Skeleton className="h-2.5 w-2/3 rounded-full bg-zinc-200" />
        </div>
      ))}
    </div>
  );
}

function TableRowSkeleton({ rowIndex }: { rowIndex: number }) {
  return (
    <div className="flex h-[46px] items-center border-b border-zinc-100 bg-white px-2">
      {SKELETON_HEADERS.map((header, cellIndex) => {
        const compact = ["", "checkbox", "No.", "수정"].includes(header.label);
        const widthClass = compact
          ? "w-5"
          : cellIndex % 3 === 0
            ? "w-2/3"
            : "w-4/5";

        return (
          <div
            key={`${rowIndex}-${header.label}-${cellIndex}`}
            className="flex min-w-0 items-center justify-center px-1"
            style={{ flex: `${getColumnFlex(header.width)} 1 0` }}
          >
            <Skeleton
              className={`h-3 rounded-full bg-zinc-200 ${widthClass}`}
            />
          </div>
        );
      })}
    </div>
  );
}

function TableSectionSkeleton({
  title,
  muted = false,
  rows = 8,
}: {
  title: string;
  muted?: boolean;
  rows?: number;
}) {
  return (
    <section className="flex flex-col">
      <div
        className={`grid grid-cols-3 rounded-t-lg px-10 py-2 text-center ${
          muted ? "bg-zinc-200" : "bg-[#DDE8F8]"
        }`}
      >
        <div className="flex items-center gap-2">
          <Skeleton className="size-2.5 rounded-full bg-white/85" />
          <Skeleton className="h-3.5 w-7 rounded-full bg-white/85" />
        </div>
        <div className="flex justify-center">
          <Skeleton className="h-4 w-24 rounded-full bg-white/85" />
          <span className="sr-only">{title}</span>
        </div>
        <div />
      </div>
      <div className="overflow-hidden border-x border-b border-zinc-100">
        <TableHeaderSkeleton />
        {Array.from({ length: rows }).map((_, index) => (
          <TableRowSkeleton key={index} rowIndex={index} />
        ))}
      </div>
    </section>
  );
}

export default function RosterManagementSkeleton() {
  return (
    <div className="w-full min-w-0">
      <div className="flex w-full flex-col items-center gap-2 px-10">
        <div className="flex w-full min-w-0 flex-col">
          <div className="flex justify-between">
            <Skeleton className="h-7 w-28 rounded-full bg-zinc-200" />
          </div>

          <HeaderSkeleton />

          <div className="flex flex-col gap-6">
            <TableSectionSkeleton title="활성 근무자" rows={4} />
            <TableSectionSkeleton title="비활성 근무자" muted rows={2} />
          </div>
        </div>
      </div>
    </div>
  );
}
